import React, { useEffect, useRef, useState } from 'react';

import classNames from 'classnames';

import { decimalUpTo } from 'Helpers/currencyFormat';

import Button from 'CommonComponents/Button';
import LoadingSpinner from 'CommonComponents/LoadingSpinner';
import ModalHeader from 'CommonComponents/ModalHeader';

export default function QuantitySourcePicker({
    sourcesUrl,
    linkUrl,
    amountSlotIndex = 0,
    venueId = 0,
    currentLink = null,
    deferred = false,
    onSelected,
    onLinked,
    onClose,
}) {
    const [groups, setGroups] = useState([]);
    const [drivers, setDrivers] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [search, setSearch] = useState('');
    const [linking, setLinking] = useState(false);
    const [selected, setSelected] = useState(null);
    const [activeTab, setActiveTab] = useState('all');
    const searchRef = useRef(null);

    useEffect(() => {
        setLoading(true);
        axios
            .get(sourcesUrl)
            .then(({ data }) => {
                setGroups(data.revenue_lines ?? []);
                setDrivers(data.drivers ?? null);
            })
            .catch(() => {
                setError($t('Failed to load sources. Please try again.'));
            })
            .finally(() => {
                setLoading(false);
                setTimeout(() => searchRef.current?.focus(), 50);
            });
    }, [sourcesUrl]);

    const handleConfirmLink = () => {
        if (!selected) return;

        // Deferred (create) mode: report the selection to the caller instead of
        // performing the link now — there is no expense to link to yet.
        if (deferred) {
            onSelected && onSelected({ ...selected, amountSlotIndex });
            return;
        }

        setLinking(true);
        const payload = {
            source_type: selected.sourceType,
            source_id: selected.sourceId,
            amount_slot_index: amountSlotIndex,
        };
        if (venueId > 0) {
            payload.venue_id = venueId;
        }
        if (selected.driverKey) {
            payload.driver = selected.driverKey;
        }
        axios
            .post(linkUrl, payload)
            .then(({ data }) => {
                onLinked && onLinked(data.expense);
            })
            .catch((err) => {
                const message = err?.response?.data?.message ?? $t('An error occurred. Please try again.');
                setError(message);
            })
            .finally(() => {
                setLinking(false);
            });
    };

    const matchesSearch = (label, full_label) => {
        if (!search) return true;
        const haystack = [label, full_label].filter(Boolean).join(' ').toLowerCase();
        return search
            .toLowerCase()
            .split(/\s+/)
            .filter(Boolean)
            .every((term) => haystack.includes(term));
    };

    const filteredGroups = groups
        .map((dept) => ({
            ...dept,
            groups: dept.groups
                .map((group) => ({
                    ...group,
                    items: group.items.filter((item) => matchesSearch(item.label, item.full_label)),
                }))
                .filter((group) => group.items.length > 0),
        }))
        .filter((dept) => dept.groups.length > 0);

    const buildDriverItems = () => {
        if (!drivers) return [];
        const sections = [];

        if (drivers.client_groups?.length > 0) {
            const items = [];
            drivers.client_groups.forEach((cat) => {
                if (cat.total && matchesSearch(cat.total.label, cat.total.full_label)) {
                    items.push(cat.total);
                }
                (cat.subcategories || []).forEach((sub) => {
                    if (matchesSearch(sub.label, sub.full_label)) items.push(sub);
                });
            });
            if (items.length > 0) sections.push({ label: $t('Client Groups'), items });
        }

        if (drivers.venues) {
            const items = ['total', 'competition', 'training']
                .filter((k) => drivers.venues[k] && matchesSearch(drivers.venues[k].label, drivers.venues[k].full_label))
                .map((k) => drivers.venues[k]);
            if (items.length > 0) sections.push({ label: $t('Venues'), items });
        }

        if (drivers.event_days?.total && matchesSearch(drivers.event_days.total.label, drivers.event_days.total.full_label)) {
            sections.push({ label: $t('Event Days'), items: [drivers.event_days.total] });
        }

        if (drivers.athletes) {
            const items = [];
            if (drivers.athletes.total && matchesSearch(drivers.athletes.total.label, drivers.athletes.total.full_label)) items.push(drivers.athletes.total);
            (drivers.athletes.by_sport || []).forEach((s) => {
                if (matchesSearch(s.label, s.full_label)) items.push(s);
            });
            if (items.length > 0) sections.push({ label: $t('Athletes'), items });
        }

        if (drivers.workforce) {
            const items = [];
            if (drivers.workforce.total && matchesSearch(drivers.workforce.total.label, drivers.workforce.total.full_label))
                items.push(drivers.workforce.total);
            (drivers.workforce.by_department || []).forEach((d) => {
                if (matchesSearch(d.label, d.full_label)) items.push(d);
            });
            if (items.length > 0) sections.push({ label: $t('Workforce Headcount'), items });
        }

        return sections;
    };

    const driverSections = buildDriverItems();

    const showRevenue = activeTab === 'all' || activeTab === 'revenue';
    const showDrivers = activeTab === 'all' || activeTab === 'drivers';

    const hasRevenue = filteredGroups.length > 0;
    const hasDrivers = driverSections.length > 0;

    const isItemSelected = (sourceType, sourceId, driverKey) => {
        if (!selected) return false;
        return selected.sourceType === sourceType && selected.sourceId === sourceId && selected.driverKey === (driverKey ?? null);
    };

    const isCurrentlyLinked = (sourceType, sourceId, driverKey) => {
        if (!currentLink) return false;
        return (
            currentLink.sourceType === sourceType && String(currentLink.sourceId) === String(sourceId) && (currentLink.driver ?? null) === (driverKey ?? null)
        );
    };

    const handleSelectItem = (sourceType, sourceId, label, value, sublabel, driverKey) => {
        setSelected({ sourceType, sourceId, label, value, sublabel, driverKey: driverKey ?? null });
    };

    const formatValue = (val) => {
        if (val === null || val === undefined) return '—';
        return decimalUpTo(val);
    };

    return (
        <div className="modal__box tw-z-50 tw-flex !tw-bg-black/50" style={{ display: 'flex', ...(deferred ? { zIndex: 60 } : {}) }}>
            <div className="modal__container">
                <div className="modal__inner-container tw-flex tw-flex-col tw-max-h-[80vh] !tw-p-0">
                    <div className="tw-p-4 tw-pb-0">
                        <ModalHeader title={$t('Link Quantity to an embedded source')} close={onClose} />
                    </div>

                    <div className="tw-flex tw-flex-col tw-gap-3 tw-px-4 tw-pb-3 tw-shrink-0">
                        <input
                            ref={searchRef}
                            type="text"
                            className="text-input"
                            placeholder={$t('Search sources...')}
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />

                        <div className="tw-flex tw-gap-1.5">
                            {[
                                { key: 'all', label: $t('All sources') },
                                { key: 'revenue', label: $t('Revenue') },
                                { key: 'drivers', label: $t('Non-financial drivers') },
                            ].map((tab) => (
                                <button
                                    key={tab.key}
                                    type="button"
                                    onClick={() => setActiveTab(tab.key)}
                                    className={classNames(
                                        'tw-px-3 tw-py-1 tw-text-xs tw-rounded-full tw-border tw-font-semibold tw-transition-colors',
                                        activeTab === tab.key
                                            ? 'tw-bg-blue tw-text-white tw-border-blue'
                                            : 'tw-bg-white tw-text-charcoal-75 tw-border-charcoal-25 hover:tw-border-blue hover:tw-text-blue',
                                    )}
                                >
                                    {tab.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="tw-overflow-y-auto tw-flex-1 tw-min-h-0 tw-px-4">
                        {loading && <LoadingSpinner size="sm" />}

                        {error && <p className="modal__input-error-text">{error}</p>}

                        {!loading && !error && !hasRevenue && !hasDrivers && (
                            <p className="tw-text-center modal__text tw-py-8 tw-text-charcoal-50">{$t('No sources available')}</p>
                        )}

                        {!loading && showRevenue && hasRevenue && (
                            <div className="tw-mb-3">
                                <p className="modal__heading tw-text-2xs tw-uppercase tw-tracking-widest tw-text-charcoal-50 tw-mb-1">{$t('Revenue Links')}</p>
                                {filteredGroups.map((dept, di) => (
                                    <div key={di}>
                                        {dept.groups.map((group, gi) => (
                                            <div key={gi}>
                                                {group.items.map((item, ii) => {
                                                    const active = isItemSelected(item.source_type, item.source_id, null);
                                                    const linked = isCurrentlyLinked(item.source_type, item.source_id, null);
                                                    return (
                                                        <button
                                                            key={ii}
                                                            type="button"
                                                            disabled={linking}
                                                            className={classNames(
                                                                'tw-w-full tw-text-left tw-px-3 tw-py-2 tw-rounded-lg tw-flex tw-items-center tw-justify-between tw-text-sm disabled:tw-opacity-50 tw-transition-colors',
                                                                active
                                                                    ? 'tw-bg-blue-15 tw-ring-1 tw-ring-blue'
                                                                    : linked
                                                                      ? 'tw-bg-green-25/50 tw-ring-1 tw-ring-green'
                                                                      : 'hover:tw-bg-grey/50',
                                                            )}
                                                            onClick={() =>
                                                                handleSelectItem(
                                                                    item.source_type,
                                                                    item.source_id,
                                                                    item.label,
                                                                    item.value,
                                                                    dept.department || group.label || null,
                                                                    null,
                                                                )
                                                            }
                                                        >
                                                            <span className="tw-flex tw-flex-col tw-min-w-0 tw-mr-3">
                                                                <span className="tw-text-charcoal tw-font-semibold tw-truncate">{item.label}</span>
                                                                {(dept.department || group.label) && (
                                                                    <span className="tw-text-2xs tw-text-charcoal-50 tw-truncate">
                                                                        {[dept.department, group.label].filter(Boolean).join(' / ')}
                                                                    </span>
                                                                )}
                                                            </span>
                                                            <span className="tw-flex tw-items-center tw-gap-2 tw-shrink-0">
                                                                {linked && <CurrentlyLinkedBadge />}
                                                                {item.badge === 'sponsor' ? <SponsorBadge /> : <RevenueBadge />}
                                                                <span className="tw-text-charcoal-75 tw-text-xs tw-font-semibold tw-tabular-nums">
                                                                    {formatValue(item.value)}
                                                                </span>
                                                            </span>
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        ))}
                                    </div>
                                ))}
                            </div>
                        )}

                        {!loading && showDrivers && hasDrivers && (
                            <div>
                                <p className="modal__heading tw-text-2xs tw-uppercase tw-tracking-widest tw-text-charcoal-50 tw-mb-1">
                                    {$t('Non-Financial Drivers')}
                                </p>
                                {driverSections.map((section, si) => (
                                    <div key={si}>
                                        {section.items.map((item, ii) => {
                                            const driverKey = item.meta?.driver ?? null;
                                            const active = isItemSelected(item.source_type, item.source_id, driverKey);
                                            const linked = isCurrentlyLinked(item.source_type, item.source_id, driverKey);
                                            return (
                                                <button
                                                    key={ii}
                                                    type="button"
                                                    disabled={linking}
                                                    className={classNames(
                                                        'tw-w-full tw-text-left tw-px-3 tw-py-2 tw-rounded-lg tw-flex tw-items-center tw-justify-between tw-text-sm disabled:tw-opacity-50 tw-transition-colors',
                                                        active
                                                            ? 'tw-bg-blue-15 tw-ring-1 tw-ring-blue'
                                                            : linked
                                                              ? 'tw-bg-green-25/50 tw-ring-1 tw-ring-green'
                                                              : 'hover:tw-bg-grey/50',
                                                    )}
                                                    onClick={() =>
                                                        handleSelectItem(item.source_type, item.source_id, item.label, item.value, section.label, driverKey)
                                                    }
                                                >
                                                    <span className="tw-flex tw-flex-col tw-min-w-0 tw-mr-3">
                                                        <span className="tw-text-charcoal tw-font-semibold tw-truncate">{item.label}</span>
                                                        <span className="tw-text-2xs tw-text-charcoal-50 tw-truncate">{section.label}</span>
                                                    </span>
                                                    <span className="tw-flex tw-items-center tw-gap-2 tw-shrink-0">
                                                        {linked && <CurrentlyLinkedBadge />}
                                                        <DriverBadge />
                                                        <span className="tw-text-charcoal-75 tw-text-xs tw-font-semibold tw-tabular-nums">
                                                            {formatValue(item.value)}
                                                        </span>
                                                    </span>
                                                </button>
                                            );
                                        })}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="tw-shrink-0 tw-p-4 tw-pt-3 tw-flex tw-flex-col tw-gap-3">
                        {selected && (
                            <div className="tw-rounded-lg tw-bg-blue-15/50 tw-px-3 tw-py-2">
                                <p className="modal__text tw-text-xs">
                                    <span className="tw-font-semibold">{$t('Selected')}:</span> <span>{selected.label}</span>
                                    {selected.value !== null && selected.value !== undefined && (
                                        <span className="tw-text-charcoal-50">
                                            {', '}
                                            {$t('current value')} {formatValue(selected.value)}
                                        </span>
                                    )}
                                </p>
                            </div>
                        )}

                        <div className="modal__footer">
                            <Button variant="secondary" onClick={onClose} disabled={linking}>
                                {$t('Cancel')}
                            </Button>
                            {selected && (
                                <Button variant="primary" onClick={handleConfirmLink} disabled={linking} loading={linking}>
                                    {deferred ? $t('Use this source') : $t('Link Source')}
                                </Button>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

function RevenueBadge() {
    return (
        <span className="tw-inline-flex tw-items-center tw-px-1.5 tw-py-0.5 tw-rounded tw-text-3xs tw-font-bold tw-tracking-wide tw-uppercase tw-bg-green-25 tw-text-charcoal-75">
            {$t('Revenue')}
        </span>
    );
}

function DriverBadge() {
    return (
        <span className="tw-inline-flex tw-items-center tw-px-1.5 tw-py-0.5 tw-rounded tw-text-3xs tw-font-bold tw-tracking-wide tw-uppercase tw-bg-blue-15 tw-text-blue-75">
            {$t('Driver')}
        </span>
    );
}

function SponsorBadge() {
    return (
        <span className="tw-inline-flex tw-items-center tw-px-1.5 tw-py-0.5 tw-rounded tw-text-3xs tw-font-bold tw-tracking-wide tw-uppercase tw-bg-blue-25 tw-text-charcoal-75">
            {$t('Sponsor')}
        </span>
    );
}

function CurrentlyLinkedBadge() {
    return (
        <span className="tw-inline-flex tw-items-center tw-px-1.5 tw-py-0.5 tw-rounded tw-text-3xs tw-font-bold tw-tracking-wide tw-uppercase tw-bg-green-25 tw-text-green-75">
            {$t('Linked')}
        </span>
    );
}
